import { closeBrackets } from '@codemirror/autocomplete';
import {
  bracketMatching,
  HighlightStyle,
  LanguageSupport,
  LRLanguage,
  syntaxHighlighting
} from '@codemirror/language';
import { styleTags, tags as t } from '@lezer/highlight'; // Styling rules
import { parser } from '@lezer/json'; // Lezer JSON parser

// Define Syntax Highlighting Rules for JSONata
const jsonataHighlighting = HighlightStyle.define([
  { tag: t.comment, color: '#6a737d', fontStyle: 'italic' }, // Comments
  { tag: t.string, color: '#032f62' }, // Strings
  { tag: t.number, color: '#005cc5' }, // Numbers
  { tag: t.bool, color: '#d73a49' }, // Booleans: true, false
  { tag: t.null, color: '#d73a49' }, // Null value
  { tag: t.keyword, color: '#d73a49' }, // JSON-like keywords
  { tag: t.variableName, color: '#e36209' }, // Variables prefixed with "$"
  { tag: t.function(t.propertyName), color: '#6f42c1' }, // Functions, e.g., "sum"
  { tag: t.operatorKeyword, color: '#d73a49' }, // Operators like "and", "or"
  { tag: t.bracket, color: '#24292e' } // Brackets: (), {}, []
]);

// Define the extended JSONata grammar with support for comments and syntax
const jsonataParser = parser.configure({
  props: [
    styleTags({
      LineComment: t.comment, // Map `//` as `t.comment`
      BlockComment: t.comment, // Map `/* */` as `t.comment`
      String: t.string, // Strings
      Number: t.number, // Numbers
      Boolean: t.bool, // Booleans
      Null: t.null, // Null
      Keyword: t.keyword, // e.g., JSONata keywords like "in", "and", "exists"
      VariableName: t.variableName, // Variables prefixed with "$"
      Function: t.function(t.propertyName), // Functions (e.g., `sum`)
      OperatorKeyword: t.operatorKeyword, // Keywords like "+", "-", etc.
      Bracket: t.bracket, // Matching brackets
      'True False': t.bool, // Map true/false to Boolean
      ArithmeticOperator: t.arithmeticOperator, // Arithmetic operators
      Operator: t.operator, // Operators
      Variable: t.variableName, // Map variables properly
      Identifier: t.name, // Identifiers
      '( )': t.paren, // Parentheses
      '[ ]': t.squareBracket, // Square brackets
      '{ }': t.brace // Braces
    })
  ]
});

// Define the complete Language + Syntax Highlighting Support
const JsonataLanguage = LRLanguage.define({
  parser: jsonataParser,
  languageData: {
    commentTokens: { line: '//', block: { open: '/*', close: '*/' } } // Define comment formats
  }
});

// Combine Syntax Highlighting and Bracket Matching
export function initializeJsonataEditor() {
  return new LanguageSupport(JsonataLanguage, [
    syntaxHighlighting(jsonataHighlighting), // Attach highlight styles
    bracketMatching(), // Enable bracket matching
    closeBrackets() // Auto-close selected brackets
  ]);
}

export const initialAiJSONataPrompt = (mockData: string) => `
You are an expert coding assistant for JSONata and JSON data transformations.

**You can help with**: JSONata expressions, JSON manipulation, data transformations, JavaScript/TypeScript functions for JSON, and debugging.

**Stay focused on**: Coding and data-related questions only. Decline non-coding questions politely.

Always use the exact structure of the provided JSON data in your examples and answers. For example, if the data is { "data": { "question": 2 } }, access the question as data.question, not just question.

**Important JSONata Features to Remember**:

Take note JSONata uses implicit arrays:
- Singleton Equivalence: Any non-array value is considered equivalent to an array containing just that value. This allows a single query to work whether it finds one item or many, without changing the syntax.
- Sequence Flattening: JSONata internally uses "sequences." If a query matches multiple values, they are returned as a flat array. If it matches only one value, it returns that value directly (a singleton sequence).
- Implicit Mapping: When an expression is applied to an array, JSONata implicitly maps that expression over every item in the array. For example, if Addresses is an array, Addresses.City will return an array of all city values.
- Predicate Logic: When using filters (predicates), JSONata implicitly applies the $boolean() function to the results. If a filter generates an array of booleans, it evaluates to true if any value in that array is true.

Do utilize these features to write concise and efficient JSONata expressions. Always use the simplest JSONata expressions possible to achieve the desired result.

JSONata basics:
- Use dot notation to access fields (e.g., data.name)
- Use [] for arrays (e.g., data.items[0])
- Common functions: $contains, $map, $filter, $sum, $count, $substring
- Only use $map() when you need custom transformations or complex logic

Examples:
- If the data contains a nested responses array, to get all question1 values, use simple dot notation responses.data.question1
- Filter and transform: responses[$exists(data.question1)].data.question1
- Complex mapping: $map(responses, function($v) { $v.data.question1 * 2 })

Here is the JSON data to use for all examples and explanations: ${mockData}

For more, see: https://jsonata.org/docs/

`;

//Here are some suggested JSONata functions to get you started: ${JSON.stringify(JsonataSuggestions.map((s) => s.label))}
